import { MenuItem } from '@/pages/layout/components/sider/menuList';
interface PathFindProps {
  menus: any[];
  path: string;
  keys?: string[];
  field?: string;
}
// 根据path 获取menu对象（支持子路径匹配）
export function findMenuByPath(config: PathFindProps): MenuItem[] {
  const { menus, keys = [], path } = config;

  // 首先尝试精确匹配
  for (const menu of menus) {
    if (menu.path === path) {
      return [...keys, menu];
    }
  }

  // 然后尝试子路径匹配
  for (const menu of menus) {
    // 子路径匹配：如果当前路径以菜单路径开头，且菜单路径不是根路径
    if (menu.path && menu.path !== '/' && path.startsWith(menu.path + '/')) {
      const result = [...keys, menu];

      // 为特定的子路由添加详情页面包屑项
      if (path.includes('/detail/')) {
        const detailItem = {
          itemKey: path,
          text: getDetailPageTitle(path),
          path: path,
          icon: undefined,
          items: undefined,
        };
        result.push(detailItem);
      }

      return result;
    }
  }

  // 最后尝试递归查找
  for (const menu of menus) {
    if (menu.items && menu.items.length > 0) {
      const result = findMenuByPath({
        menus: menu.items,
        path,
        keys: [...keys, menu],
      });
      if (result.length > 0) {
        return result;
      }
    }
  }

  return [];
}

// 获取详情页标题的辅助函数
function getDetailPageTitle(path: string): string {
  if (path.includes('/alibaba/detail/')) {
    return 'web.alibaba.detail.title';
  }
  if (path.includes('/cate/detail/')) {
    return 'web.cate.detail.title';
  }
  // 可以根据需要添加更多的详情页类型
  return 'web.common.detail';
}

// 根据path 获取menu field（支持子路径匹配）
export function findMenuFieldByPath(config: PathFindProps): MenuItem[] {
  const { menus, keys = [], path, field = 'itemKey' } = config;
  for (const menu of menus) {
    // 精确匹配
    if (menu.path === path) {
      return [...keys, menu[field]];
    }
    // 子路径匹配：如果当前路径以菜单路径开头，且菜单路径不是根路径
    if (menu.path && menu.path !== '/' && path.startsWith(menu.path + '/')) {
      return [...keys, menu[field]];
    }
    if (menu.items && menu.items.length > 0) {
      const result = findMenuFieldByPath({
        menus: menu.items,
        path,
        field,
        keys: [...keys, menu[field]],
      });
      if (result.length === 0) {
        continue;
      }
      return result;
    }
  }
  return [];
}

// 分割数字或者字符串
export function numberSplit(
  value: string | number,
  carry = 3,
  split = ','
): string {
  let result = '';
  value = value.toString();
  for (let i = 1; i <= value.length; i++) {
    const index = value.length - i;
    const str =
      i % carry === 0 && index !== 0 ? split + value[index] : value[index];
    result = str + result;
  }
  return result || value;
}

/**
 * 将数组转换为指定分隔符的字符串
 * @param array 要转换的数组或字符串
 * @param separator 分隔符，默认为逗号
 * @returns 转换后的字符串，如果数组为空或无效则返回空字符串
 */
export function arrayToString(
  array: any[] | string | undefined | null,
  separator = ','
): string {
  // 如果是字符串，直接返回
  if (typeof array === 'string') {
    return array;
  }

  // 如果不是数组或数组为空，返回空字符串
  if (!Array.isArray(array) || array.length === 0) {
    return '';
  }

  return array.join(separator);
}

// 格式化日期为 YYYY-MM-DD 格式
export const formatDate = (
  date:
    | Date
    | string
    | number
    | null
    | undefined
    | { format: (formatStr: string) => string },
  fallback = '-'
): string => {
  try {
    // 如果是空值，直接返回 "-"
    if (!date) {
      return fallback;
    }

    // 兼容 Semi UI DatePicker / dayjs / moment：优先使用其 format 能力
    if (typeof (date as any)?.format === 'function') {
      return (date as any).format('YYYY-MM-DD');
    }

    const dateInput = date as Date | string | number;

    // 尝试创建 Date 对象
    const dateObj = new Date(dateInput);

    // 检查日期是否有效
    if (isNaN(dateObj.getTime())) {
      return fallback;
    }

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    // 解析失败时返回 "-"
    return fallback;
  }
};

/**
 * 防抖函数
 * @param func 要防抖的函数
 * @param delay 延迟时间，单位毫秒
 * @returns 返回防抖后的函数
 */
export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  delay: number
): T => {
  let timeoutId: NodeJS.Timeout;
  return ((...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  }) as T;
};
